<template>
  <panel-layout
    :heading="heading"
    :loading="loading"
    :didAttemptToDismiss="cancel"
  >
    <div class="h-full">
      <o-scroll class="h-full">
        <div class="min-h-full flex flex-col">
          <waitlist-alert :waitlist="staging.waitlist" />
          <move-alert :reservation="staging.move" />
          <course-run-alert :courseRun="courseRun" :course="course" />

          <o-steps ref="steps" :progress="progress" class="flex flex-col p-4">
            <o-step v-slot="{ isActive }" :name="$t('general.customer')" :info="isMove ? 'Appointment customer' : 'Select a customer'" :success="isCompleteCustomer" :class="{ 'hidden' : isCustomerTypeGroup }">
              <reservation-create-customer v-if="!isMove" v-model:customer="_customer" :isActive="isActive" />
              <div v-else-if="!isCustomerTypeGroup" class="w-full flex items-center text-left" :class="{ 'ring-offset-2 ring-4 rounded-md' : isActive }">
                <o-avatar :items="[staging.customer]" size="md" border="" class="flex-shrink-0" />
                <div class="ml-2 text-sm font-normal">
                  <p class="leading-none">
                    {{ staging.customer.firstName }} {{ staging.customer.lastName }}
                  </p>
                  <p v-if="staging.customer.email" class="mt-1 block text-xs text-gray-700 dark:text-gray-200 leading-none">
                    {{ staging.customer.email }}
                  </p>
                </div>
              </div>
            </o-step>
            <o-step v-slot="{ state, isActive }" :name="$t('general.services')" :info="$t('pages.appointment.create.servicesInfo')" :success="isCompleteServices">
              <reservation-create-services v-model:staging="staging" :state="state" :isActive="isActive" />
            </o-step>
            <o-step v-slot="{ state, isActive }" :name="$t('general.availability')" :info="$t('pages.appointment.create.availabilityInfo')" :success="isCompleteAvailability">
              <reservation-create-availability v-model:staging="staging" :state="state" :isActive="isActive" v-model:loading="loadingAvailability" />
            </o-step>
            <o-step v-slot="{ state, isActive }" :class="{ 'hidden' : !staging.recurrence.enabled }" :name="$t('reservation.recurrence')" :info="isMove ? $t('pages.appointment.create.futureRecurring') : $t('pages.appointment.create.setToRepeat')" :success="isCompleteRecurrence && isCompleteAvailability">
              <reservation-create-recurrence v-model:staging="staging" :state="state" :isActive="isActive" />
            </o-step>
            <o-step v-if="$features.all(['passes', 'vouchers'])" v-slot="{ state, isActive }" :class="{ 'hidden' : isCustomerTypeGroup }" :name="$t('pages.appointment.create.passesPromoCodes')" :info="$t('pages.appointment.create.passesPromoCodesInfo')">
              <reservation-create-discounts v-model:staging="staging" :state="state" :isActive="isActive" />
            </o-step>
            <o-step v-if="!isCourseSession" v-slot="{ state, isActive }" :name="$t('general.notes')" :info="$t('pages.appointment.create.notesInfo')">
              <reservation-create-notes v-model:staging="staging" :state="state" :isActive="isActive" />
            </o-step>
            <o-step v-slot="{ state, isActive }" :name="$t('general.confirm')" :info="$t('pages.appointment.create.confirmInfo')">
              <reservation-create-confirm v-model:staging="staging" :state="state" :isActive="isActive" :courseRun="courseRun" />
            </o-step>
          </o-steps>
        </div>
      </o-scroll>
    </div>

    <template #footer>
      <div class="flex items-center justify-end space-x-2">
        <o-button flat outline @click="cancel()">
          {{ $t('general.cancel') }}
        </o-button>
        <o-button variant="primary" icon="save" :loading="loadingAvailability" :disabled="!allowSave" @click="save()">
          {{ confirmLabel }}
        </o-button>
      </div>
    </template>

    <!--
      Unsaved-changes dialog for the panel Cancel button / dismiss-by-swipe.
      Bound via the `confirmDialog` ref that `cancel()` already awaits on.
      Mirrors the pattern used by customer/edit/index.vue and similar panels.
    -->
    <o-dialog
      ref="confirmDialog"
      :title="$t('general.unsavedChanges')"
      :info="$t('general.unsavedChangesInfo')"
      :options="[
        { text: $t('general.back'), type: 'cancel' },
        { text: $t('general.closeWithoutSaving'), type: 'confirm', variant: undefined, flat: true, outline: true, callback: () => cancel(true) },
        { text: $t('general.saveAndClose'), type: 'confirm', response: 'save', callback: () => save() },
      ]"
      :portal="panel.portal"
    />
  </panel-layout>
</template>
<script setup lang="ts">
// @ts-ignore
import ReservationCreateCustomer from '@/components/reservation/create/Customer.vue'
// @ts-ignore
import ReservationCreateServices from '@/components/reservation/create/Services.vue'
// @ts-ignore
import ReservationCreateAvailability from '@/components/reservation/create/Availability.vue'
// @ts-ignore
import ReservationCreateRecurrence from '@/components/reservation/create/Recurrence.vue'
// @ts-ignore
import ReservationCreateDiscounts from '@/components/reservation/create/Discounts.vue'
// @ts-ignore
import ReservationCreateNotes from '@/components/reservation/create/Notes.vue'
// @ts-ignore
import ReservationCreateConfirm from '@/components/reservation/create/Confirm.vue'

// @ts-ignore
import WaitlistAlert from '@/components/reservation/create/Waitlist.vue'
// @ts-ignore
import MoveAlert from '@/components/reservation/create/Move.vue'
// @ts-ignore
import CourseRunAlert from '@/components/reservation/create/CourseRun.vue'

// @ts-ignore
import PanelLayout from '@/components/panel/PanelLayout.vue'

// @ts-ignore
import {
  OSteps,
  OStep,
  OAvatar,
} from '@/components/elements'

// @ts-ignore
import {
  ToggleField,
} from '@/components/fields'

import { ReservationCustomerRequestType } from '~/_api/models/reservation'
import { ConfirmationRequest } from '~/_api/models/booking'
import { ServiceCustomerType } from '~/_api/models/service'

import { StagingRequest } from '~/components/reservation/create/provider'

import { usePanel } from '~/composables/usePanel'

const panel = usePanel()

const componentProps = withDefaults(defineProps<{
  type?: string
  presets?: Record<string, any>
  course?: Record<string, any> | null
  courseRun?: Record<string, any> | null
  callback?: Function
}>(), {
  presets: () => ({}),
  course: null,
  courseRun: null,
  callback: () => () => {},
})

const { $api, $notification, $bus, $i18n, $auth, $date } = useNuxtApp()

const steps = ref<any>()
const confirmDialog = ref<any>()

const staging = ref<any>(null)
const loading = ref(true)
const loadingAvailability = ref(false)

const _customer = computed({
  get () {
    return staging.value.customer
  },
  set (value: any) {
    staging.value.customer = value
  },
})

const heading = computed(() => {
  if (isCourseSession.value) {
    return 'Add Course Session'
  }

  if (isCustomerTypeGroup.value) {
    return $i18n.t('pages.appointment.create.addClass')
  }

  if (isWaitlist.value) {
    return $i18n.t('pages.appointment.create.addFromWaitlist')
  }

  if (isMove.value) {
    return $i18n.t('pages.appointment.create.moveExisting')
  }

  return $i18n.t('pages.appointment.create.add')
})

const allowSave = computed(() => progress.value === 7 && !loadingAvailability.value)

const confirmLabel = computed(() => {
  if (loadingAvailability.value) {
    return $i18n.t('general.loading')
  }
  if (!staging.value.isCompleteCustomer) {
    return $i18n.t('pages.appointment.create.selectCustomer')
  }
  if (!staging.value.isCompleteServices) {
    return $i18n.t('pages.appointment.create.selectService')
  }
  if (!staging.value.isCompleteAvailability) {
    return $i18n.t('pages.appointment.create.selectTime')
  }
  if (!staging.value.isCompleteRecurrence) {
    return $i18n.t('pages.appointment.create.selectRecurrence')
  }

  if (isCourseSession.value) {
    return 'Add course session'
  }

  if (isMove.value) {
    return 'Move appointment'
  }

  return !isMove.value ? $i18n.t('pages.appointment.create.confirm') : $i18n.t('reservation.moveAppointment')
})

const progress = computed(() => staging.value.progress)
const isCustomerTypeSingle = computed(() => staging.value.isCustomerTypeSingle)
const isCustomerTypeGroup = computed(() => staging.value?.isCustomerTypeGroup)
const isCompleteCustomer = computed(() => staging.value.isCompleteCustomer)
const isCompleteServices = computed(() => staging.value.isCompleteServices)
const isCompleteAvailability = computed(() => staging.value.isCompleteAvailability)
const isCompleteRecurrence = computed(() => staging.value.isCompleteRecurrence)
const isManualAvailability = computed(() => staging.value.isManualAvailability)
const isNewCustomer = computed(() => staging.value.isNewCustomer)
const isEmployeeRequest = computed(() => staging.value.isEmployeeRequest)
const isWaitlist = computed(() => !!staging.value?.waitlist)
const isMove = computed(() => !!staging.value?.move)
const isCourseSession = computed(() => !!componentProps.courseRun)
const hasService = computed(() => staging.value.hasService)
const hasGroupService = computed(() => staging.value.hasGroupService)

const saveCustomer = async () => {
  try {
    const customer = (await $api.customer.save(staging.value.customer)).data

    if (customer) {
      staging.value.customer = customer

      $notification.toast({
        title: $i18n.t('customer.createdSuccess'),
        text: $i18n.t('customer.createdSuccessInfo', { name: customer.displayName }),
        variant: 'success',
      })

      $bus.emit.created('Customer', customer)

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

const createReservation = async () => {
  const { availabilitySlot, options, overrideTime, sendEmail } = staging.value

  try {
    const request = ConfirmationRequest.fromSlot(availabilitySlot, options)
    const reservation = (await $api.booking.confirm(request, { overrideTime, sendEmail })).data

    if (reservation) {
      $notification.toast({
        title: $i18n.t('pages.appointment.create.success'),
        text: $i18n.t('pages.appointment.create.successInfo', { label: reservation.customerInfoLabel }),
        variant: 'success',
      })

      $bus.emit.created('Reservation', reservation)

      if (!isCourseSession.value) {
        panel.replace('appointment/[id]', {
          id: String(reservation.idReservation),
          date: String(reservation.date),
        })
      } else {
        panel.close()
      }

      return reservation
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const moveReservation = async () => {
  const { availabilitySlot, options, overrideTime, sendEmail, resetReminder, recurrence } = staging.value

  try {
    const request = ConfirmationRequest.fromSlot(availabilitySlot, options)
    const future = recurrence.enabled
    const reservation = (await $api.booking.move(request, { overrideTime, sendEmail, resetReminder, future })).data

    if (reservation) {
      $notification.toast({
        title: $i18n.t('reservation.moved'),
        text: $i18n.t('pages.appointment.create.movedSuccessInfo', { label: reservation.customerInfoLabel }),
        variant: 'success',
      })

      $bus.emit.updated('Reservation', reservation)
      panel?.close()

      return reservation
    }
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const save = async () => {
  loading.value = true

  const _isNewCustomer = isNewCustomer.value
  const _isEmployeeRequest = isEmployeeRequest.value

  if (!_isNewCustomer && _isEmployeeRequest) {
    staging.value.customerRequestType = ReservationCustomerRequestType.request
  }
  if (_isNewCustomer && !_isEmployeeRequest) {
    staging.value.customerRequestType = ReservationCustomerRequestType.newClient
  }
  if (_isNewCustomer && _isEmployeeRequest) {
    staging.value.customerRequestType = ReservationCustomerRequestType.newClientRequest
  }
  if (!_isNewCustomer && !_isEmployeeRequest) {
    staging.value.customerRequestType = ReservationCustomerRequestType.transient
  }

  if (isCustomerTypeSingle.value && _isNewCustomer) {
    const customer = await saveCustomer()

    if (!customer) {
      loading.value = false

      return
    }
  }

  let reservation

  if (isMove.value) {
    reservation = await moveReservation()
  } else {
    reservation = await createReservation()
  }

  if (reservation) {
    componentProps.callback?.(reservation)
  }

  loading.value = false
}

const cancel = async (force = false) => {
  if (!force) {
    const response = await confirmDialog.value?.open()

    if (response === 'confirm') {
      cancel(true)
    }

    if (response === 'save') {
      save()
    }
  } else {
    panel.close(force)
  }
}

// created logic
loading.value = false

const query = componentProps.type
const customerType = (query && ServiceCustomerType[query as any]) || ServiceCustomerType.single

const presets = componentProps.presets

staging.value = new StagingRequest({ $notification, $bus, $i18n, $auth, $date }, { customerType, presets })

if (presets && presets.waitlist) {
  staging.value.fromWaitlist(presets.waitlist)
}

if (presets && presets.reservation) {
  staging.value.fromReservation(presets.reservation)
}

if (presets && presets.move) {
  staging.value.fromMove(presets.move)
}
</script>
